import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import type { PresenceElement } from "./types";

interface AgentPresenceContextValue {
  elements: PresenceElement[];
  addPresence: (el: PresenceElement) => void;
  removePresence: (id: string) => void;
  dismissPresence: (id: string) => void;
}

const AgentPresenceContext = createContext<AgentPresenceContextValue>({
  elements: [],
  addPresence: () => {},
  removePresence: () => {},
  dismissPresence: () => {},
});

export function AgentPresenceProvider({ children }: { children: React.ReactNode }) {
  const [elements, setElements] = useState<PresenceElement[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Poll /api/presence for updates
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch("/api/presence");
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (Array.isArray(data.elements)) {
            setElements(data.elements);
          }
        }
      } catch {
        // Ignore polling errors
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Auto-cleanup ephemeral elements (whispers, toasts with duration)
  useEffect(() => {
    for (const el of elements) {
      if (
        el.duration &&
        (el.kind === "whisper" || el.kind === "toast") &&
        !timers.current.has(el.id)
      ) {
        const remaining = el.duration - (Date.now() - new Date(el.createdAt).getTime());
        if (remaining > 0) {
          timers.current.set(
            el.id,
            setTimeout(() => {
              setElements((prev) => prev.filter((e) => e.id !== el.id));
              timers.current.delete(el.id);
            }, remaining),
          );
        }
      }
    }
    return () => {
      for (const timer of timers.current.values()) clearTimeout(timer);
    };
  }, [elements]);

  const addPresence = useCallback((el: PresenceElement) => {
    setElements((prev) => [...prev.filter((e) => e.id !== el.id), el]);
  }, []);

  const removePresence = useCallback((id: string) => {
    setElements((prev) => prev.filter((e) => e.id !== id));
    if (timers.current.has(id)) {
      clearTimeout(timers.current.get(id));
      timers.current.delete(id);
    }
  }, []);

  const dismissPresence = useCallback(
    (id: string) => {
      // Remove locally and notify server
      removePresence(id);
      fetch(`/api/presence/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(() => {});
    },
    [removePresence],
  );

  return (
    <AgentPresenceContext.Provider
      value={{ elements, addPresence, removePresence, dismissPresence }}
    >
      {children}
    </AgentPresenceContext.Provider>
  );
}

export function useAgentPresence() {
  return useContext(AgentPresenceContext);
}
